/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Popover,
  Grid,
  InputAdornment,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { SketchPicker } from "react-color";
import { styled } from "@mui/material/styles";
import { useEventData } from "../EventDataContext";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#666",
  },
}));

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const ColorPicker = ({
  title,
  colors,
  selected,
  onSelect,
  onAdd,
  addButtonRef,
}) => (
  <Box display="flex" flexDirection="column" gap={1} flex={1}>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      color="#4A4A4A"
      sx={{ textAlign: "left", fontSize: "14px" }}
    >
      {title}
    </Typography>
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      {colors.map((color) => (
        <Box
          key={color}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: color,
            cursor: "pointer",
            border: selected === color ? `2px solid ${color}` : "1px solid #E0E0E0",
            boxShadow: selected === color ? "0 0 0 2px white, 0 0 0 4px #8A5918" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
          onClick={() => onSelect(color)}
        >
          {selected === color && (
            <CheckIcon sx={{ color: "#fff", fontSize: 18, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }} />
          )}
        </Box>
      ))}
      <IconButton
        sx={{ 
          border: "1px dashed #8A5918", 
          borderRadius: "50%",
          width: 36,
          height: 36,
          padding: 0,
        }}
        onClick={onAdd}
        ref={addButtonRef}
      >
        <AddIcon sx={{ color: "#8A5918", fontSize: 20 }} />
      </IconButton>
    </Box>
  </Box>
);

const fonts = [
  { name: "maaja", fontFamily: "'maaja', cursive" },
  { name: "K2D", fontFamily: "'K2D', sans-serif" },
  { name: "Mali", fontFamily: "'Mali', cursive" },
  { name: "Noto Sans", fontFamily: "'Noto Sans', sans-serif" },
];

const BorderIconWrapper = ({ children, selected }) => (
  <Box
    sx={{
      width: 80,
      height: 80,
      border: selected ? "2px solid #8A5918" : "1px solid #E0E0E0",
      borderRadius: "12px",
      background: selected ? "#FFF8F0" : "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      transition: "all 0.2s ease",
    }}
  >
    {children}
    {selected && (
      <Box
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#8A5918",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <CheckIcon sx={{ color: "white", fontSize: 14 }} />
      </Box>
    )}
  </Box>
);

const FancyBorderIcon = ({ selected }) => (
  <BorderIconWrapper selected={selected}>
    <Box
      sx={{
        width: 40,
        height: 40,
        border: "2px dashed #8A5918",
        borderRadius: "8px",
        background: "none",
      }}
    />
  </BorderIconWrapper>
);

const RoundedBorderIcon = ({ selected }) => (
  <BorderIconWrapper selected={selected}>
    <Box
      sx={{
        width: 40,
        height: 40,
        border: "2px solid #8A5918",
        borderRadius: "50%",
        background: "none",
      }}
    />
  </BorderIconWrapper>
);

const StampBorderIcon = ({ selected }) => (
  <BorderIconWrapper selected={selected}>
    <Box
      sx={{
        width: 40,
        height: 40,
        border: "2px dotted #8A5918",
        borderRadius: "4px",
        background: "none",
      }}
    />
  </BorderIconWrapper>
);

const borderStyles = [
  { id: "fancy", component: FancyBorderIcon },
  { id: "rounded", component: RoundedBorderIcon },
  { id: "stamp", component: StampBorderIcon },
];

export default function ThemeManagementPage() {
  const { eventData, updateNestedEventData, uploadImageAndUpdateData, validationErrors } = useEventData();
  
  const themeData = eventData.theme;
  const mainColor = themeData.mainColor?.value;
  const secondaryColor = themeData.secondaryColor?.value;
  const backgroundColor = themeData.backgroundColor?.value;
  const fontColor = themeData.fontColor?.value;
  const backgroundImage = themeData.backgroundImage?.value || null;
  const selectedFont = themeData.selectedFont?.value || "maaja";
  const headerFontSize = themeData.headerFontSize?.value || 32;
  const detailFontSize = themeData.detailFontSize?.value || 16;
  const buttonFontSize = themeData.buttonFontSize?.value || 18;
  const selectedBorderStyle = themeData.borderStyle?.value || "fancy";

  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerTarget, setPickerTarget] = useState("");
  const [newColor, setNewColor] = useState("#ffffff");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  
  const defaultColors = ["#7B3F00", "#4B5B63", "#A34BFF"];
  const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));
  const [mainColors, setMainColors] = useState(unique([themeData.mainColor?.value, ...defaultColors]));
  const [secondaryColors, setSecondaryColors] = useState(unique([themeData.secondaryColor?.value, ...defaultColors]));
  const [backgroundColors, setBackgroundColors] = useState(unique([themeData.backgroundColor?.value, ...defaultColors]));
  const [fontColors, setFontColors] = useState(unique([themeData.fontColor?.value, ...defaultColors]));

  const addButtonRefs = {
    main: useRef(null),
    secondary: useRef(null),
    background: useRef(null),
    fontColor: useRef(null),
  };

  React.useEffect(() => {
    if (mainColor && !mainColors.includes(mainColor)) {
      setMainColors((prev) => unique([mainColor, ...prev]));
    }
    if (secondaryColor && !secondaryColors.includes(secondaryColor)) {
      setSecondaryColors((prev) => unique([secondaryColor, ...prev]));
    }
    if (backgroundColor && !backgroundColors.includes(backgroundColor)) {
      setBackgroundColors((prev) => unique([backgroundColor, ...prev]));
    }
    if (fontColor && !fontColors.includes(fontColor)) {
      setFontColors((prev) => unique([fontColor, ...prev]));
    }
  }, [mainColor, secondaryColor, backgroundColor, fontColor]);

  const handleOpenPicker = (target) => {
    setPickerTarget(target);
    setAnchorEl(addButtonRefs[target].current);
  };

  const handleAddColor = () => {
    if (pickerTarget === "main") {
      setMainColors((prev) => unique([...prev, newColor]));
      updateNestedEventData("theme", "mainColor", { value: newColor });
    } else if (pickerTarget === "secondary") {
      setSecondaryColors((prev) => unique([...prev, newColor]));
      updateNestedEventData("theme", "secondaryColor", { value: newColor });
    } else if (pickerTarget === "background") {
      setBackgroundColors((prev) => unique([...prev, newColor]));
      updateNestedEventData("theme", "backgroundColor", { value: newColor });
    } else if (pickerTarget === "fontColor") {
      setFontColors((prev) => unique([...prev, newColor]));
      updateNestedEventData("theme", "fontColor", { value: newColor });
    }
    setAnchorEl(null);
    setNewColor("#ffffff");
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingImage(true);
      try {
        const result = await uploadImageAndUpdateData("theme", "backgroundImage", file);
        if (!result.success) {
          if (!result.error.includes('size')) {
            const reader = new FileReader();
            reader.onloadend = () => {
              updateNestedEventData("theme", "backgroundImage", { value: reader.result });
            };
            reader.readAsDataURL(file);
          } else {
             setErrorMessage(result.error);
             setShowError(true);
          }
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred while uploading the image");
        setShowError(true);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const removeImage = () => {
    updateNestedEventData("theme", "backgroundImage", { value: null });
  };

  const handleColorSelect = (colorType, color) => {
    updateNestedEventData("theme", colorType, { value: color });
  };

  const handleFontSizeChange = (fontType, value) => {
    updateNestedEventData("theme", fontType, { value: parseInt(value) || 0 });
  };

  const hasError = (fieldName) => {
    return validationErrors?.theme?.[fieldName];
  };

  const getErrorMessage = (fieldName) => {
    return validationErrors?.theme?.[fieldName] || '';
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      <SectionTitle>Color Theme</SectionTitle>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <ColorPicker
            title="Main Color"
            colors={mainColors}
            selected={mainColor}
            onSelect={(color) => handleColorSelect("mainColor", color)}
            onAdd={() => handleOpenPicker("main")}
            addButtonRef={addButtonRefs.main}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            title="Secondary Color"
            colors={secondaryColors}
            selected={secondaryColor}
            onSelect={(color) => handleColorSelect("secondaryColor", color)}
            onAdd={() => handleOpenPicker("secondary")}
            addButtonRef={addButtonRefs.secondary}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            title="Background Color"
            colors={backgroundColors}
            selected={backgroundColor}
            onSelect={(color) => handleColorSelect("backgroundColor", color)}
            onAdd={() => handleOpenPicker("background")}
            addButtonRef={addButtonRefs.background}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ColorPicker
            title="Font Color"
            colors={fontColors}
            selected={fontColor}
            onSelect={(color) => handleColorSelect("fontColor", color)}
            onAdd={() => handleOpenPicker("fontColor")}
            addButtonRef={addButtonRefs.fontColor}
          />
        </Grid>
      </Grid>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <SectionTitle>Background Image</SectionTitle>
      <Box
        sx={{
          position: "relative",
          backgroundColor: "#F9FAFB",
          border: "2px dashed #E0E0E0",
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#8A5918",
            backgroundColor: "#FFF8F0",
          },
        }}
      >
        {backgroundImage ? (
          <>
            <img
              src={backgroundImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
            <IconButton
              onClick={removeImage}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <AttachFileIcon sx={{ color: "#9CA3AF", fontSize: 48, mb: 2 }} />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="#4A4A4A"
              mb={0.5}
            >
              Click to upload image
            </Typography>
            <Typography variant="body2" color="#9CA3AF" mb={3}>
              SVG, PNG, JPG or GIF (max. 800KB)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingImage}
              sx={{
                color: "#8A5918",
                borderColor: "#8A5918",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6b3b07",
                  backgroundColor: "rgba(138, 89, 24, 0.04)",
                },
              }}
            >
              {uploadingImage ? "Uploading..." : "Browse File"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploadingImage}
              />
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
            <Box>
              <SectionTitle>Typography</SectionTitle>
              <Box display="flex" gap={1.5} flexWrap="wrap" mb={4}>
                {fonts.map((font) => {
                  const isSelected = font.name === selectedFont;
                  return (
                    <Box
                      key={font.name}
                      onClick={() => updateNestedEventData("theme", "selectedFont", { value: font.name })}
                      sx={{
                        cursor: "pointer",
                        px: 2.5,
                        py: 1,
                        borderRadius: "100px",
                        backgroundColor: isSelected ? "#8A5918" : "white",
                        border: isSelected ? "1px solid #8A5918" : "1px solid #E0E0E0",
                        color: isSelected ? "white" : "#4A4A4A",
                        fontFamily: font.fontFamily,
                        fontSize: "16px",
                        fontWeight: 500,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#8A5918",
                          color: isSelected ? "white" : "#8A5918",
                        },
                      }}
                    >
                      {font.name}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Box sx={{ my: 2 }}>
                <Divider />
              </Box>
              <SectionTitle>Border Style</SectionTitle>
              <Box display="flex" gap={3}>
                {borderStyles.map((style) => (
                  <Box
                    key={style.id}
                    onClick={() => updateNestedEventData("theme", "borderStyle", { value: style.id })}
                    sx={{ cursor: "pointer" }}
                  >
                    <style.component selected={selectedBorderStyle === style.id} />
                    <Typography
                      variant="caption"
                      display="block"
                      textAlign="left"
                      mt={1}
                      color={selectedBorderStyle === style.id ? "#8A5918" : "#666"}
                      fontWeight={selectedBorderStyle === style.id ? 600 : 400}
                    >
                      {style.id.charAt(0).toUpperCase() + style.id.slice(1)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item md={2} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
          <Divider orientation="vertical" sx={{ height: "100%" }} />
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionTitle>Font Sizes</SectionTitle>
          <Box display="flex" flexDirection="column" gap={2}>
            {[
              { label: "Header Size", value: headerFontSize, key: "headerFontSize" },
              { label: "Detail Size", value: detailFontSize, key: "detailFontSize" },
              { label: "Button Size", value: buttonFontSize, key: "buttonFontSize" },
            ].map(({ label, value, key }) => (
              <Box key={key}>
                <Typography variant="caption" color="#666" fontWeight={600} mb={0.5} display="block">
                  {label}
                </Typography>
                <StyledTextField
                  value={value}
                  onChange={(e) => handleFontSizeChange(key, e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">px</InputAdornment>
                    ),
                  }}
                  error={hasError(key)}
                  helperText={getErrorMessage(key)}
                />
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box p={2}>
          <SketchPicker
            color={newColor}
            onChangeComplete={(color) => setNewColor(color.hex)}
          />
          <Box mt={2} textAlign="right">
            <Button variant="contained" onClick={handleAddColor} sx={{ bgcolor: "#8A5918", "&:hover": { bgcolor: "#6b3b07" } }}>
              Add
            </Button>
          </Box>
        </Box>
      </Popover>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
